import { useState, useRef, useEffect, type ReactNode, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  delay?: number;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  contentClassName?: string;
  maxWidth?: string;
  disabled?: boolean;
}

export function Tooltip({
  children,
  content,
  delay = 200,
  position = 'top',
  className,
  contentClassName,
  maxWidth = '320px',
  disabled = false,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const padding = 8;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - padding;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + padding;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - padding;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + padding;
        break;
    }

    // Keep within viewport
    const viewportPadding = 8;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding));
    top = Math.max(viewportPadding, Math.min(top, window.innerHeight - tooltipRect.height - viewportPadding));

    setCoords({ top, left });
  }, [position]);

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isVisible, updatePosition]);

  const showTooltip = () => {
    if (disabled) return;
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={clsx('inline-block', className)}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            className={clsx(
              'fixed z-50 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg shadow-lg animate-in fade-in zoom-in-95 duration-150',
              contentClassName
            )}
            style={{
              top: coords.top,
              left: coords.left,
              maxWidth,
            }}
          >
            {content}
            {/* Arrow */}
            <div
              className={clsx(
                'absolute w-2 h-2 bg-gray-900 rotate-45',
                position === 'top' && 'bottom-[-4px] left-1/2 -translate-x-1/2',
                position === 'bottom' && 'top-[-4px] left-1/2 -translate-x-1/2',
                position === 'left' && 'right-[-4px] top-1/2 -translate-y-1/2',
                position === 'right' && 'left-[-4px] top-1/2 -translate-y-1/2'
              )}
            />
          </div>,
          document.body
        )}
    </>
  );
}

// Simple helper for info tooltips with icon
interface InfoTooltipProps {
  content: ReactNode;
  className?: string;
}

export function InfoTooltip({ content, className }: InfoTooltipProps) {
  return (
    <Tooltip content={content}>
      <span
        className={clsx(
          'inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-xs cursor-help hover:bg-gray-300 transition-colors',
          className
        )}
      >
        ?
      </span>
    </Tooltip>
  );
}

// Rich tooltip for metrics with title and description
interface MetricTooltipProps {
  title: string;
  description: string;
  value?: string | number;
  subtext?: string;
  children: ReactNode;
}

export function MetricTooltip({ title, description, value, subtext, children }: MetricTooltipProps) {
  return (
    <Tooltip
      content={
        <div className="space-y-2">
          <p className="font-semibold">{title}</p>
          <p className="text-xs text-gray-300">{description}</p>
          {value !== undefined && (
            <p className="text-xs pt-1 border-t border-gray-700">
              {value}
              {subtext && <span className="text-gray-400 ml-1">{subtext}</span>}
            </p>
          )}
        </div>
      }
      maxWidth="280px"
    >
      <span className="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
        {children}
      </span>
    </Tooltip>
  );
}
